/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Box } from '@hover/blueprint';
import React from 'react';
import type { ReactNode } from 'react';
import { useHistory } from 'react-router';

import { Button, Link as SharedLink } from 'src/shared/components';
import {
  useProfileQuery,
  usePublicOrgInfoQuery,
} from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useLocation,
  useTranslate,
} from 'src/shared/hooks';
import { Route } from 'src/shared/route';
import { Global } from 'src/shared/telemetry';
import { urlWithRedirectTo } from 'src/shared/utils/routing';

import { PageContainer } from '../Layout/PageContainer';

import { CobrandedLogo } from './CobrandedLogo';

export const loginPaths = [
  '/onboarding/welcome',
  '/onboarding/password',
  '/onboarding/login/sso',
];

const StyledNavBar = ({
  redirectPath = Route.PROPERTIES.path,
  cobrandingAffiliateOrgId,
  signupUrl,
  children,
  colorMode = 'light',
}: {
  redirectPath?: string;
  children?: ReactNode;
  signupUrl?: string; // optional path, if provided, a signup button will show
  cobrandingAffiliateOrgId?: string | null; // org ID that overrides the user's org id for cobranding
  colorMode?: 'light' | 'dark';
}) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const { pathname, search } = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(search);

  if (cobrandingAffiliateOrgId && !searchParams.get('aff_org_id')) {
    searchParams.append('aff_org_id', cobrandingAffiliateOrgId);
  }

  const loginUrl = `${Route.SIGN_UP_ONBOARDING.path}/welcome`;

  const { data: profileData } = useProfileQuery();

  const { data: partnerData } = usePublicOrgInfoQuery({
    variables: {
      publicIdentifier: cobrandingAffiliateOrgId || '',
    },
    skip: !cobrandingAffiliateOrgId,
  });

  const user = profileData?.profile?.user;
  const userOrg = profileData?.profile?.user?.orgs[0];

  const cobranding = {
    logoUrl:
      (partnerData?.publicOrgInfo?.brandLogoUrl || userOrg?.brand?.logoUrl) ??
      undefined,
    logoUrlInvert:
      (partnerData?.publicOrgInfo?.brandLogoUrlInvert ||
        userOrg?.brand?.placesLogoUrl) ??
      undefined,
    displayName:
      (partnerData?.publicOrgInfo?.displayName || userOrg?.name) ?? undefined,
  };

  return (
    <PageContainer
      backgroundColor={colorMode === 'dark' ? 'neutral.900' : 'neutral.0'}
      maxWidth="initial"
    >
      <Box
        alignItems="center"
        height={525}
        justifyContent="space-between"
        justifyItems="center"
        width={1}
      >
        <SharedLink
          aria-label={t('navigation.navigateToHome')}
          isExternal
          marginRight={700}
          pageName={Global.NAV}
          target="_self"
          testId="hover-logo"
          to={redirectPath}
        >
          <CobrandedLogo
            cobranding={cobranding}
            colorMode={colorMode}
            height={300}
          />
        </SharedLink>
        <Box flex={1} justifyContent="space-between">
          {children}
        </Box>
        {!user && !loginPaths.includes(pathname) && (
          <Button
            as="a"
            colorMode={colorMode}
            href={urlWithRedirectTo(
              loginUrl,
              history.location.pathname + search,
              search,
            )}
            variant="ghost"
          >
            {t('login')}
          </Button>
        )}
        {!user && signupUrl && (
          <Button
            as="a"
            colorMode={colorMode}
            href={urlWithRedirectTo(
              signupUrl,
              history.location.pathname + search,
              search,
            )}
            variant="secondary"
          >
            {t('onboarding.signup')}
          </Button>
        )}
      </Box>
    </PageContainer>
  );
};

export { StyledNavBar };
